<script lang="ts">
  import { findIndex, keyBy } from 'lodash-es'
  import { isPlainObject } from '#app/lib/boolean_tests'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { newError } from '#app/lib/error'
  import { onChange } from '#app/lib/svelte/svelte'
  import InventoryBrowser from '#inventory/components/inventory_browser.svelte'
  import { getInventoryView } from '#inventory/components/lib/inventory_browser_helpers'
  import type { FocusedSection, SerializedShelfOrWithoutShelf } from '#inventory/types/inventory_browser'
  import type { GroupId } from '#server/types/group'
  import ShelfBox from '#shelves/components/shelf_box.svelte'
  import ShelvesSection from '#shelves/components/shelves_section.svelte'
  import { getShelvesByOwner, type SerializedShelf } from '#shelves/lib/shelves'
  import type { SerializedUser } from '#users/lib/users'

  let {
    user,
    selectedShelf = $bindable(),
    groupId,
    flash = $bindable(),
    focusedSection,
    showShelfFollowers,
  }: {
    user: SerializedUser
    selectedShelf?: SerializedShelfOrWithoutShelf
    groupId?: GroupId
    flash?: FlashState
    focusedSection: FocusedSection
    showShelfFollowers: boolean
  } = $props()

  // Keep track of the selected shelf as an index number rather than a shelf object
  // to be able to bind to shelves[selectedShelfIndex], and directly update the array object
  let selectedShelfIndex: number | 'without-shelf' = $state()

  function onSelectShelf (newSelectedShelf: SerializedShelfOrWithoutShelf) {
    if (isPlainObject(newSelectedShelf)) {
      selectedShelfIndex = findIndex(shelves, shelf => shelf._id === newSelectedShelf._id)
    } else if (newSelectedShelf === 'without-shelf') {
      selectedShelfIndex = 'without-shelf'
    } else {
      throw newError('selected shelf not found', 500, { newSelectedShelf, shelves })
    }
    focusedSection = { type: 'shelf' }
  }
  function onCloseShelf () {
    selectedShelfIndex = null
    focusedSection = { type: 'user' }
  }

  let shelves: SerializedShelf[] = $state([])
  let waitForShelves: Promise<void> = $state()
  $effect(() => {
    onChange(user, () => {
      waitForShelves = getShelvesByOwner(user._id)
        .then(res => {
          shelves = res
          if (selectedShelf != null) onSelectShelf(selectedShelf)
        })
        .catch(err => flash = err)
    })
  })

  const itemsShelvesByIds = $derived(keyBy(shelves, '_id'))
</script>

<ShelvesSection
  {waitForShelves}
  {user}
  {shelves}
  {onSelectShelf}
/>

{#if selectedShelfIndex === 'without-shelf'}
  <ShelfBox
    withoutShelf={true}
    {onCloseShelf}
    {user}
    {itemsShelvesByIds}
    {focusedSection}
    {onSelectShelf}
  />
{:else if selectedShelfIndex != null}
  {#key selectedShelfIndex}
    <ShelfBox
      bind:shelf={shelves[selectedShelfIndex]}
      {onCloseShelf}
      {user}
      {itemsShelvesByIds}
      {focusedSection}
      {onSelectShelf}
      {showShelfFollowers}
    />
  {/key}
{:else}
  <InventoryBrowser
    inventoryViewPromise={getInventoryView('user', user)}
    ownerId={user._id}
    {groupId}
    isMainUser={user.isMainUser}
    {itemsShelvesByIds}
    {onSelectShelf}
  />
{/if}
